import { Vec3, Quat, clamp, damp } from './math.js';
import { Track, TrackProjection, MAIN_SPLINE } from './track.js';
import { VehicleStats } from './vehicles.js';
import { MAX_SPEED_ABS } from './constants.js';

// ─── Input ──────────────────────────────────────────────────────────────────

export interface InputFrame {
  /** -1..1 */
  steer: number;
  /** -1..1 airborne pitch (+ = nose down) */
  pitch: number;
  /** 0..1 */
  throttle: number;
  /** 0..1 left airbrake */
  brakeL: number;
  /** 0..1 right airbrake */
  brakeR: number;
  boost: boolean;
  respawn: boolean;
}

export function emptyInput(): InputFrame {
  return { steer: 0, pitch: 0, throttle: 0, brakeL: 0, brakeR: 0, boost: false, respawn: false };
}

export function copyInput(dst: InputFrame, src: InputFrame): void {
  dst.steer = src.steer;
  dst.pitch = src.pitch;
  dst.throttle = src.throttle;
  dst.brakeL = src.brakeL;
  dst.brakeR = src.brakeR;
  dst.boost = src.boost;
  dst.respawn = src.respawn;
}

export function sanitizeInput(i: InputFrame): void {
  i.steer = clamp(Number.isFinite(i.steer) ? i.steer : 0, -1, 1);
  i.pitch = clamp(Number.isFinite(i.pitch) ? i.pitch : 0, -1, 1);
  i.throttle = clamp(Number.isFinite(i.throttle) ? i.throttle : 0, 0, 1);
  i.brakeL = clamp(Number.isFinite(i.brakeL) ? i.brakeL : 0, 0, 1);
  i.brakeR = clamp(Number.isFinite(i.brakeR) ? i.brakeR : 0, 0, 1);
  i.boost = !!i.boost;
  i.respawn = !!i.respawn;
}

// ─── Ship state ─────────────────────────────────────────────────────────────

export const SHIP_RADIUS = 2.1;
export const HOVER_HEIGHT = 1.25;
const ATTACH_HEIGHT = 7.5;
const ATTACH_MARGIN = 5;
const GRAVITY = 26;
const HOVER_SPRING = 45;
const HOVER_DAMP = 11;
const PAD_IMPULSE = 16;
const PAD_ENERGY = 0.22;
const DRAFT_RANGE = 30;
const DRAFT_LATERAL = 4.5;

export interface ShipEventSink {
  onWallHit?(ship: ShipState, intensity: number): void;
  onShipHit?(a: ShipState, b: ShipState, intensity: number): void;
  onBoostPad?(ship: ShipState): void;
  onLand?(ship: ShipState, intensity: number): void;
  onRespawnStart?(ship: ShipState): void;
  onFallOff?(ship: ShipState): void;
}

export class ShipState {
  // identity
  index: number;

  // kinematics
  pos = new Vec3();
  vel = new Vec3();
  up = new Vec3(0, 1, 0);
  forward = new Vec3(0, 0, 1);
  yawVel = 0;

  // track binding
  splineId = MAIN_SPLINE;
  s = 0;
  lateral = 0;
  height = 0;
  trackWidth = 24;
  grounded = true;
  inJump = false;

  // resources
  boostEnergy = 0.5;
  boosting = false;
  drifting = false;
  draftFactor = 0; // 0..1 slipstream strength this tick

  // recovery
  respawnTimer = 0; // ticks remaining frozen
  protectTimer = 0; // spawn protection ticks
  fallTimer = 0;

  // fx bookkeeping (also replicated for remote rendering)
  wallContact = 0; // decaying 0..1
  lastLandIntensity = 0;
  padCooldown = 0;

  // race bookkeeping lives in race.ts but mirrored here for convenience
  finished = false;
  eliminated = false;

  constructor(index: number, public stats: VehicleStats) {
    this.index = index;
  }

  speed(): number {
    return this.vel.length();
  }

  forwardSpeed(): number {
    return this.vel.dot(this.forward);
  }

  basis(outQuat: Quat, tmpRight: Vec3): Quat {
    tmpRight.cross(this.up, this.forward).normalize();
    return outQuat.setFromBasis(tmpRight, this.up, this.forward);
  }
}

// scratch vectors (module-level: sim is single-threaded on both server and client)
const frame = { pos: new Vec3(), tangent: new Vec3(), up: new Vec3(), right: new Vec3(), width: 24 };
const proj: TrackProjection = {
  splineId: 0,
  s: 0,
  lateral: 0,
  height: 0,
  distSq: 0,
  progress: 0,
  width: 24,
};
const vLat = new Vec3();
const vTmp = new Vec3();
const vTmp2 = new Vec3();
const surfaceVel = new Vec3();

export interface StepContext {
  track: Track;
  dt: number;
  events?: ShipEventSink;
}

/**
 * Advance one ship a single fixed timestep. Deterministic: no randomness, no allocation.
 * Returns the ship's updated track projection (progress used by race logic).
 */
export function stepShip(ship: ShipState, input: InputFrame, ctx: StepContext): number {
  const { track, dt } = ctx;
  const st = ship.stats;

  if (ship.padCooldown > 0) ship.padCooldown -= dt;
  if (ship.protectTimer > 0) ship.protectTimer -= dt;
  ship.wallContact = Math.max(0, ship.wallContact - dt * 3);

  // ── respawn freeze ──
  if (ship.respawnTimer > 0) {
    ship.respawnTimer -= dt;
    ship.vel.set(0, 0, 0);
    if (ship.respawnTimer <= 0) {
      ship.respawnTimer = 0;
      ship.protectTimer = 2;
      ship.vel.copy(ship.forward).scale(18);
    }
    return track.progressOf(ship.splineId, ship.s);
  }

  // ── project onto track ──
  track.project(ship.pos, { splineId: ship.splineId, s: ship.s }, proj);
  ship.splineId = proj.splineId;
  ship.s = proj.s;
  ship.lateral = proj.lateral;
  ship.height = proj.height;
  ship.trackWidth = proj.width;

  const spline = track.spline(ship.splineId);
  spline.frameAt(ship.s, frame);

  ship.inJump = track.isInJumpZone(ship.splineId, ship.s);
  const withinRibbon = Math.abs(ship.lateral) <= proj.width / 2 + ATTACH_MARGIN;
  const wasGrounded = ship.grounded;
  // the re-attach window reaches a little BELOW the deck: landing arcs that come in
  // slightly under a rising road get scooped back onto it instead of falling forever
  const attached = !ship.inJump && withinRibbon && ship.height > -6.5 && ship.height < ATTACH_HEIGHT;
  ship.grounded = attached && ship.height < HOVER_HEIGHT * 2.4;

  // ── orientation ──
  // up vector follows the track frame when attached; relaxes toward world-up when far away
  const upRate = attached ? 14 : ship.inJump && withinRibbon ? 6 : 2.2;
  const upT = 1 - Math.exp(-upRate * dt);
  vTmp.copy(attached || (ship.inJump && withinRibbon) ? frame.up : WORLD_UP);
  ship.up.lerp(vTmp, upT).normalize();

  // steering
  const speed = ship.speed();
  const speedSteerFalloff = 1 / (1 + speed / 150);
  const brakeYaw = (input.brakeR - input.brakeL) * 1.15;
  ship.drifting = input.brakeL > 0.25 || input.brakeR > 0.25;
  let targetYaw = input.steer * st.turnRate * speedSteerFalloff + brakeYaw * speedSteerFalloff;
  if (!attached) targetYaw *= 0.55 + st.airControl * 0.3;
  ship.yawVel = damp(ship.yawVel, targetYaw, st.steerResponse, dt);
  // Handedness: a POSITIVE rotation around `up` swings the nose toward up×forward,
  // which is the pilot's LEFT in our right-handed world. Steer > 0 must turn RIGHT,
  // so the applied rotation is negated. (Bots/track internals are convention-agnostic;
  // this sign is the one place the math meets a human's hands.)
  rotateAround(ship.forward, ship.up, -ship.yawVel * dt);

  // airborne pitch control
  if (!ship.grounded) {
    vTmp.cross(ship.up, ship.forward).normalize(); // right
    rotateAround(ship.forward, vTmp, input.pitch * st.airControl * 1.5 * dt);
    // pitching drags `up` along so the craft actually rotates
    rotateAround(ship.up, vTmp, input.pitch * st.airControl * 1.5 * dt);
  }
  // keep basis orthonormal
  ship.forward.addScaled(ship.up, -ship.up.dot(ship.forward)).normalize();

  // ── forces ──
  // Boost and drift are mutually exclusive: airbrakes spoil the thrust. This also
  // closes the perpetual-motion loop where drifting charges the meter while boost
  // spends it — you charge sideways, then straighten out and fire.
  const boostRequested = input.boost && ship.boostEnergy > 0.02 && !ship.drifting;
  ship.boosting = boostRequested;

  // thrust direction: forward, but flattened onto the surface plane when grounded
  vTmp.copy(ship.forward);
  if (ship.grounded) vTmp.addScaled(frame.up, -frame.up.dot(vTmp)).normalize();

  let thrust = input.throttle * st.acceleration;
  if (boostRequested) {
    thrust += st.boostPower;
    ship.boostEnergy = Math.max(0, ship.boostEnergy - dt / st.boostTank);
  }
  ship.vel.addScaled(vTmp, thrust * dt);

  // quadratic drag calibrated so terminal velocity == topSpeed at full throttle
  const dragK = st.acceleration / (st.topSpeed * st.topSpeed);
  const dragScale = ship.grounded ? 1 : 0.62;
  const draftRelief = 1 - 0.28 * ship.draftFactor;
  const sp2 = ship.vel.length();
  if (sp2 > 0.01) {
    ship.vel.addScaled(ship.vel, -dragK * sp2 * dragScale * draftRelief * dt);
  }

  // airbrake drag
  const abTotal = input.brakeL + input.brakeR;
  if (abTotal > 0.01) {
    const fs = ship.vel.dot(ship.forward);
    if (fs > 0) ship.vel.addScaled(ship.forward, -Math.min(fs, abTotal * 22 * dt));
  }

  // lateral grip + drift boost charging
  if (attached) {
    // decompose velocity in the surface frame
    const fSpeed = ship.vel.dot(ship.forward);
    const upSpeed = ship.vel.dot(frame.up);
    vLat.copy(ship.vel).addScaled(ship.forward, -fSpeed).addScaled(frame.up, -upSpeed);
    const slip = vLat.length();
    let gripEff = st.grip * (ship.drifting ? st.driftGrip : 1);
    // slick hazards
    for (const hz of track.hazards) {
      if (
        hz.type === 'slick' &&
        ship.splineId === MAIN_SPLINE &&
        ship.s >= hz.sStart &&
        ship.s <= hz.sEnd &&
        Math.abs(ship.lateral - (hz.lane * proj.width) / 2) < hz.halfWidth
      ) {
        gripEff *= 0.2;
      }
    }
    ship.vel.addScaled(vLat, -Math.min(1, gripEff * dt));
    // drifting with real slip charges the boost tank
    if (ship.drifting && slip > 6 && fSpeed > 20) {
      ship.boostEnergy = Math.min(1, ship.boostEnergy + slip * 0.0018 * dt * 60);
    }
  }

  // passive trickle + drafting charge
  ship.boostEnergy = Math.min(1, ship.boostEnergy + (0.014 + ship.draftFactor * 0.05) * dt);

  // hover spring / gravity
  if (attached) {
    const relVel = ship.vel.dot(frame.up);
    if (ship.grounded) {
      const err = HOVER_HEIGHT - ship.height;
      // clamp so deep scoops (landing slightly under the deck) pop up firmly, not violently
      const springAcc = clamp(err * HOVER_SPRING - relVel * HOVER_DAMP, -220, 170);
      ship.vel.addScaled(frame.up, springAcc * dt);
      if (!wasGrounded) {
        const impact = Math.max(0, -relVel);
        ship.lastLandIntensity = impact;
        if (impact > 3) ctx.events?.onLand?.(ship, impact);
      }
    } else {
      // attached but above hover band: pull down toward the surface
      ship.vel.addScaled(frame.up, -GRAVITY * dt);
    }
  } else {
    // AG lift: inside authored jump zones the craft floats — arcs stay long and
    // climbing landings remain reachable (this is what makes big jumps feel right)
    const g = ship.inJump && withinRibbon ? GRAVITY * 0.5 : GRAVITY;
    ship.vel.y -= g * dt;
  }

  // absolute speed cap
  const finalSpeed = ship.vel.length();
  if (finalSpeed > MAX_SPEED_ABS) ship.vel.scale(MAX_SPEED_ABS / finalSpeed);

  // ── integrate ──
  ship.pos.addScaled(ship.vel, dt);

  // ── walls / edges ──
  const walls = track.wallsAt(ship.splineId, ship.s);
  const halfW = proj.width / 2;
  const edge = halfW - SHIP_RADIUS * 0.7;
  if (attached) {
    if (ship.lateral > edge && walls.right) {
      wallResponse(ship, frame.right, ship.lateral - edge, ctx);
    } else if (ship.lateral < -edge && walls.left) {
      vTmp.copy(frame.right).scale(-1);
      wallResponse(ship, vTmp, -ship.lateral - edge, ctx);
    }
  }

  // barrier hazards act like a wall segment in the middle of the road
  if (attached && ship.splineId === MAIN_SPLINE) {
    for (const hz of track.hazards) {
      if (hz.type !== 'barrier') continue;
      if (ship.s >= hz.sStart && ship.s <= hz.sEnd) {
        const center = (hz.lane * proj.width) / 2;
        const d = ship.lateral - center;
        if (Math.abs(d) < hz.halfWidth + SHIP_RADIUS * 0.6) {
          const dir = d >= 0 ? 1 : -1;
          vTmp.copy(frame.right).scale(dir);
          wallResponse(ship, vTmp, hz.halfWidth + SHIP_RADIUS * 0.6 - Math.abs(d), ctx, 1.6);
        }
      }
    }
  }

  // ── boost pads ──
  if (ship.grounded && ship.padCooldown <= 0) {
    for (const pad of track.boostPads) {
      if (pad.splineId !== ship.splineId) continue;
      if (ship.s < pad.sStart || ship.s > pad.sEnd) continue;
      const center = (pad.lane * proj.width) / 2;
      if (Math.abs(ship.lateral - center) > pad.halfWidth) continue;
      ship.vel.addScaled(ship.forward, PAD_IMPULSE);
      ship.boostEnergy = Math.min(1, ship.boostEnergy + PAD_ENERGY);
      ship.padCooldown = 0.8;
      ctx.events?.onBoostPad?.(ship);
      break;
    }
  }

  // ── falling off the world ──
  const fellBelow = ship.height < -26 && !attached;
  const farOff = proj.distSq > 190 * 190;
  if (fellBelow || farOff) {
    ship.fallTimer += dt;
    if (ship.fallTimer > 0.15) {
      ctx.events?.onFallOff?.(ship);
      respawnShip(ship, track, ctx);
    }
  } else if (!attached && !ship.inJump && !withinRibbon && ship.height < -6) {
    // slid off the side without walls
    ship.fallTimer += dt;
    if (ship.fallTimer > 1.4) {
      ctx.events?.onFallOff?.(ship);
      respawnShip(ship, track, ctx);
    }
  } else {
    ship.fallTimer = 0;
  }

  // manual reset
  if (input.respawn && ship.respawnTimer <= 0) {
    respawnShip(ship, track, ctx);
  }

  return proj.progress;
}

const WORLD_UP = new Vec3(0, 1, 0);

function wallResponse(ship: ShipState, outward: Vec3, penetration: number, ctx: StepContext, mult = 1): void {
  // push back inside
  ship.pos.addScaled(outward, -penetration);
  const outVel = ship.vel.dot(outward);
  if (outVel > 0) {
    const intensity = outVel * mult;
    ship.vel.addScaled(outward, -outVel * 1.25); // slight bounce
    // scrape scrubs speed based on toughness
    const scrub = clamp((intensity / 55) * (1.35 - ship.stats.toughness), 0, 0.3);
    ship.vel.scale(1 - scrub);
    if (intensity > 2.5) {
      ship.wallContact = Math.min(1, ship.wallContact + intensity / 25);
      ctx.events?.onWallHit?.(ship, intensity);
    }
  }
  // the wall guides the nose along itself: grinding scrubs speed but never pins
  // you nose-first at a standstill (feel, and it keeps bots from stalling too)
  const fwdOut = ship.forward.dot(outward);
  if (fwdOut > 0) {
    ship.forward.addScaled(outward, -fwdOut * 0.28).normalize();
  }
}

export function respawnShip(ship: ShipState, track: Track, ctx: StepContext): void {
  // respawn at the last checkpoint line behind current progress
  const progress = track.progressOf(ship.splineId, ship.s);
  let cpS = 0;
  for (const cp of track.checkpoints) {
    if (cp <= progress - 4) cpS = cp;
  }
  if (!track.main.closed && progress < track.checkpoints[0] + 4) cpS = Math.max(2, progress - 8);
  const f = frame;
  track.main.frameAt(cpS, f);
  ship.splineId = MAIN_SPLINE;
  ship.s = cpS;
  ship.pos.copy(f.pos).addScaled(f.up, HOVER_HEIGHT + 0.4);
  ship.vel.set(0, 0, 0);
  ship.up.copy(f.up);
  ship.forward.copy(f.tangent);
  ship.yawVel = 0;
  ship.respawnTimer = 1.1;
  ship.fallTimer = 0;
  ship.boostEnergy = Math.max(0.25, ship.boostEnergy * 0.6);
  ctx.events?.onRespawnStart?.(ship);
}

/** Rotate vector v around unit axis by angle (Rodrigues). */
function rotateAround(v: Vec3, axis: Vec3, angle: number): void {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const dot = v.dot(axis);
  vTmp2.cross(axis, v);
  v.set(
    v.x * cos + vTmp2.x * sin + axis.x * dot * (1 - cos),
    v.y * cos + vTmp2.y * sin + axis.y * dot * (1 - cos),
    v.z * cos + vTmp2.z * sin + axis.z * dot * (1 - cos)
  );
}

/** Pairwise ship collisions + drafting. Call once per world tick after individual steps. */
export function resolveShipInteractions(ships: ShipState[], ctx: StepContext): void {
  const n = ships.length;
  for (let i = 0; i < n; i++) ships[i].draftFactor = 0;

  for (let i = 0; i < n; i++) {
    const a = ships[i];
    if (a.respawnTimer > 0 || a.eliminated) continue;
    for (let j = i + 1; j < n; j++) {
      const b = ships[j];
      if (b.respawnTimer > 0 || b.eliminated) continue;

      // collision
      const distSq = a.pos.distanceToSq(b.pos);
      const minDist = SHIP_RADIUS * 2;
      if (distSq < minDist * minDist && distSq > 1e-6) {
        if (a.protectTimer <= 0 && b.protectTimer <= 0) {
          const dist = Math.sqrt(distSq);
          vTmp.copy(b.pos).sub(a.pos).scale(1 / dist); // a → b normal
          const overlap = minDist - dist;
          const ma = a.stats.mass;
          const mb = b.stats.mass;
          const total = ma + mb;
          a.pos.addScaled(vTmp, (-overlap * mb) / total);
          b.pos.addScaled(vTmp, (overlap * ma) / total);
          // relative velocity along normal
          surfaceVel.copy(b.vel).sub(a.vel);
          const relN = surfaceVel.dot(vTmp);
          if (relN < 0) {
            const restitution = 0.35;
            const impulse = (-(1 + restitution) * relN) / (1 / ma + 1 / mb);
            a.vel.addScaled(vTmp, -impulse / ma);
            b.vel.addScaled(vTmp, impulse / mb);
            const intensity = -relN;
            // toughness scrub
            a.vel.scale(1 - clamp((intensity / 90) * (1.3 - a.stats.toughness), 0, 0.22));
            b.vel.scale(1 - clamp((intensity / 90) * (1.3 - b.stats.toughness), 0, 0.22));
            if (intensity > 3) ctx.events?.onShipHit?.(a, b, intensity);
          }
        }
      }

      // drafting: check both directions
      applyDraft(a, b);
      applyDraft(b, a);
    }
  }
}

function applyDraft(follower: ShipState, leader: ShipState): void {
  vTmp.copy(leader.pos).sub(follower.pos);
  const ahead = vTmp.dot(follower.forward);
  if (ahead < 4 || ahead > DRAFT_RANGE) return;
  // lateral offset from follower's heading line
  vTmp.addScaled(follower.forward, -ahead);
  const lat = vTmp.length();
  if (lat > DRAFT_LATERAL) return;
  const strength = (1 - ahead / DRAFT_RANGE) * (1 - lat / DRAFT_LATERAL);
  if (follower.speed() > 30) {
    follower.draftFactor = Math.max(follower.draftFactor, clamp(strength * 1.2, 0, 1));
  }
}
